// Single source of truth for "is this user an admin / may they do X" on the
// frontend. It mirrors the backend guards in server.py exactly:
//
//   require_superuser       -> role === 'superuser'
//   require_admin_or_above  -> role in ['admin', 'superuser']
//   check_permission /      -> admin/superuser bypass, otherwise the user's
//   require_module_access      position permissions (user.permissions, built
//                              by /api/auth/login and /api/auth/me)
//
// Nothing else grants admin rights. In particular `level`, the username or
// email containing "admin", or being the company's first user do NOT: the
// backend ignores those, so honouring them here only showed menus and
// buttons that then failed with 403.
//
// Module licences are enforced by the backend; the module list the sidebar
// shows comes from /api/modules/my, which already applies licence + position
// (and is empty for the superuser, see below).

export const ADMIN_ROLES = ['admin', 'superuser'];
const CRUD_ACTIONS = ['create', 'read', 'update', 'delete'];

export const isSuperuser = (user) => user?.role === 'superuser';

export const isAdmin = (user) => ADMIN_ROLES.includes(user?.role);

// The superuser is the platform operator, not a company. It has no licence,
// no modules (/api/modules/my returns []) and no tenant data of its own, so
// its whole UI is the platform panel.
export const SUPERUSER_HOME = '/superuser';

/** Landing page after login / when "home" is clicked. */
export const homePathFor = (user) => (isSuperuser(user) ? SUPERUSER_HOME : '/dashboard');

// Screens that only make sense inside a company: the tenant dashboard, the
// module pages, tenant billing/checkout and tenant integrations.
const TENANT_ONLY_PREFIXES = [
  '/dashboard', '/modules', '/checkout', '/license-inactive', '/payment', '/accounting',
];

export const isTenantOnlyPath = (pathname = '') =>
  TENANT_ONLY_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));

/**
 * Where to send `user` instead of `pathname`, or null to stay. Only the
 * superuser is redirected (tenant screens -> platform panel); tenant admins
 * and users are never affected.
 */
export const superuserRedirect = (user, pathname) =>
  isSuperuser(user) && isTenantOnlyPath(pathname) ? SUPERUSER_HOME : null;

const anyAction = (perms) =>
  !!perms && typeof perms === 'object' && CRUD_ACTIONS.some((a) => perms[a] === true);

// Yetki Yonetimi stores create/read/update/delete, plus a separate Onay
// right (approve) on approvable screens (config/approvable.js). Older pages
// ask for other verbs; they map onto the right the backend checks for them.
const ACTION_ALIASES = {
  edit: 'update',
  approve: 'approve',
  view: 'read',
  create_transaction: 'create',
};

// One page's right. A permission saved before the Onay right existed (or
// sent by an older backend) has no `approve`; then update decides, as the
// backend's permission_catalog.can_approve does.
const allows = (pagePerms, action) => {
  if (!pagePerms || typeof pagePerms !== 'object') return false;
  if (action === 'approve' && (pagePerms.approve === undefined || pagePerms.approve === null)) {
    return pagePerms.update === true;
  }
  return pagePerms[action] === true;
};

/**
 * Permission check, module or page level.
 *   hasPermission(user, 'inventory', 'read')
 *   hasPermission(user, 'inventory.products', 'create')
 *   hasPermission(user, 'inventory', 'products', 'create')   (same as above)
 *   hasPermission(user, 'inventory.products.create')          (same as above)
 * Without an action, any CRUD right on the target counts as access.
 * Page keys are the resource codes in config/moduleStructure.js
 * (utils/permissionKeys.test.js checks every call site).
 * user.permissions has the shape { module: { resource: {create, read, update, delete[, approve]} } }.
 */
export const hasPermission = (user, path, action, pageAction) => {
  if (isAdmin(user)) return true;
  if (!user?.permissions || !path) return false;

  let segments = path.split('.');
  if (pageAction !== undefined) {
    segments = [segments[0], action];
    action = pageAction;
  } else if (segments.length > 2 && action === undefined) {
    action = segments[2];
  }
  action = ACTION_ALIASES[action] || action;

  const [module, page] = segments;
  const modulePermissions = user.permissions[module];
  if (!modulePermissions || typeof modulePermissions !== 'object') return false;

  if (page) {
    const pagePermissions = modulePermissions[page];
    if (!pagePermissions) return false;
    return action ? allows(pagePermissions, action) : anyAction(pagePermissions);
  }

  return Object.values(modulePermissions).some((pagePerms) =>
    action ? allows(pagePerms, action) : anyAction(pagePerms)
  );
};

// Every module code an admin can reach (require_module_access bypass).
export const ALL_MODULES = [
  'hr', 'inventory', 'sales', 'purchasing', 'finance', 'accounting', 'crm',
  'service', 'assets', 'office', 'tasks', 'tender', 'production',
];

/** Modules the user can read: all for admins, else those with a read right. */
export const getAccessibleModules = (user) => {
  if (isAdmin(user)) return [...ALL_MODULES];
  if (!user?.permissions) return [];
  return Object.keys(user.permissions).filter((module) =>
    hasPermission(user, module, 'read')
  );
};
